import { Button, buttonStyles } from '@amore/ui/components/button'
import { Card, CardHeader, CardTitle } from '@amore/ui/components/card'
import { MaterialsPanel } from '@amore/ui/components/materials-panel'
import { Link, useNavigate, useParams, useRouterState } from '@tanstack/react-router'
import {
  ArrowRight,
  CalendarDays,
  FileText,
  FolderOpen,
  NotebookPen,
  Plus,
  Route,
  Users,
} from 'lucide-react'
import { useRef } from 'react'
import { formatDate, plural } from '@/lib/format'
import { useCanEdit, useWedding } from '@/mock/store'
import type { CeremonyDocument } from '@/mock/types'
import { EmptyState } from '@/patterns/empty-state'
import { CeremonyDocumentSheet } from './document-sheet'
import { CeremonyEditAction } from './edit-action'
import { CeremonyMissingSheet } from './missing-sheet'
import { ceremonySummary } from './model'
import { CeremonyNotesSheet } from './notes-sheet'
import { CeremonySummarySheet } from './summary-sheet'

export function CeremonyMaterialsPage() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const navigate = useNavigate()
  const { documentId } = useParams({ strict: false })
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const addButton = useRef<HTMLButtonElement>(null)
  const { event, venue, officiant, brideWitness, groomWitness } = ceremonySummary(data)
  const { documents, notes, steps } = data.ceremony
  const document = documents.find((item) => item.id === documentId)
  const closeDetail = (id: string) => {
    void navigate({ to: '/ceremony/materials' })
    requestAnimationFrame(() => {
      ;(
        window.document.getElementById(id) ??
        addButton.current ??
        window.document.querySelector<HTMLAnchorElement>(
          'nav[aria-label="Stránky obřadu"] a[href="/ceremony/materials"]',
        )
      )?.focus({ preventScroll: true })
    })
  }
  const openDocument = (item: CeremonyDocument) =>
    void navigate({ to: `/ceremony/materials/files/${item.id}` })
  const hasSummary = Boolean(
    event || venue || officiant || brideWitness || groomWitness || steps.length,
  )

  return (
    <>
      <Card padding="none" className="overflow-hidden">
        <CardHeader appearance="strip">
          <div className="flex items-center gap-2.5">
            <FileText className="size-4 text-action" aria-hidden="true" />
            <CardTitle as="h2">Souhrn pro den obřadu</CardTitle>
          </div>
        </CardHeader>
        <div className="space-y-5 p-5 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <p className="max-w-xl text-sm leading-6 text-muted-foreground">
              Místo, průběh, lidé a plán B v jednom přehledu pro ty, kdo vám s obřadem pomohou.
            </p>
            <Button
              id="ceremony-open-summary"
              appearance="outline"
              tone="neutral"
              onClick={() => void navigate({ to: '/ceremony/materials/summary' })}
            >
              Náhled souhrnu <ArrowRight className="size-4" aria-hidden="true" />
            </Button>
          </div>
          {hasSummary ? (
            <div className="grid gap-5 border-t border-border pt-5 md:grid-cols-3 md:divide-x md:divide-border">
              <div className="space-y-2 md:pr-4">
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CalendarDays className="size-3.5" aria-hidden="true" />
                  Kdy a kde
                </p>
                <p className="text-sm font-semibold">
                  {data.identity.date
                    ? formatDate(data.identity.date, 'long')
                    : 'Datum zatím neurčené'}
                  {event?.time ? ` · ${event.time}` : ''}
                </p>
                <p className="break-words text-sm text-muted-foreground">
                  {event?.location || venue?.name || 'Místo zatím nevybrané'}
                </p>
                <Link
                  to="/ceremony/place"
                  className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
                >
                  Místo a příprava <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </div>
              <div className="space-y-2 md:px-1">
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Route className="size-3.5" aria-hidden="true" />
                  Průběh obřadu
                </p>
                <p className="text-sm font-semibold">
                  {steps.length
                    ? `${steps.length} ${plural(steps.length, 'bod scénáře', 'body scénáře', 'bodů scénáře')}`
                    : 'Scénář zatím prázdný'}
                </p>
                <p className="text-sm text-muted-foreground">Pořadí, zapojení lidé a pokyny.</p>
                <Link
                  to="/ceremony/scenario"
                  className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
                >
                  Otevřít scénář <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </div>
              <div className="space-y-2 md:pl-1">
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Users className="size-3.5" aria-hidden="true" />
                  Lidé a role
                </p>
                <p className="break-words text-sm font-semibold">
                  {officiant?.name || 'Oddávající zatím nevybraný'}
                </p>
                <p className="break-words text-sm text-muted-foreground">
                  {[brideWitness?.name, groomWitness?.name].filter(Boolean).join(' · ') ||
                    'Svědci zatím nevybraní'}
                </p>
                <Link
                  to="/ceremony/people"
                  className={buttonStyles({ appearance: 'ghost', tone: 'neutral', size: 'sm' })}
                >
                  Otevřít lidi a role <ArrowRight className="size-3.5" aria-hidden="true" />
                </Link>
              </div>
            </div>
          ) : (
            <p className="border-t border-border pt-4 text-sm text-muted-foreground">
              Souhrn se bude skládat z údajů, které postupně doplníte do obřadu.
            </p>
          )}
        </div>
      </Card>
      <MaterialsPanel
        aria-label="Soubory a poznámky k obřadu"
        header={
          <div className="flex items-center gap-2.5">
            <FolderOpen className="size-4 text-action" aria-hidden="true" />
            <CardTitle as="h2">Soubory a poznámky</CardTitle>
          </div>
        }
        files={
          <>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h3 className="text-sm font-semibold">Soubory k obřadu</h3>
              {canEdit ? (
                <Button
                  ref={addButton}
                  appearance="outline"
                  tone="neutral"
                  size="sm"
                  onClick={() => void navigate({ to: '/ceremony/materials/files/new' })}
                >
                  <Plus className="size-3.5" aria-hidden="true" />
                  Přidat soubor
                </Button>
              ) : null}
            </div>
            {documents.length ? (
              <ul className="divide-y divide-border">
                {documents.map((document) => (
                  <li key={document.id} className="flex min-w-0 items-center gap-3 py-4 first:pt-0">
                    <span className="grid size-10 shrink-0 place-items-center rounded-full bg-muted text-muted-foreground">
                      <FileText className="size-4" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 flex-1 space-y-1">
                      <button
                        id={`ceremony-file-${document.id}`}
                        type="button"
                        onClick={() => openDocument(document)}
                        className="block max-w-full break-words text-left text-sm font-semibold text-action hover:underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-ring"
                      >
                        {document.name}
                      </button>
                      <p className="text-xs text-muted-foreground">
                        {document.name.split('.').at(-1)?.toLocaleUpperCase('cs')} ·{' '}
                        {Math.max(1, Math.round(document.sizeBytes / 1000))} kB
                      </p>
                    </div>
                    <CeremonyEditAction
                      label={`Upravit soubor ${document.name}`}
                      onClick={() => openDocument(document)}
                    />
                  </li>
                ))}
              </ul>
            ) : (
              <EmptyState
                placement="inline"
                icon={<FolderOpen />}
                title="Všechno důležité po ruce"
                description={
                  canEdit
                    ? 'Proslov, nákres prostoru nebo domluva s oddávající. Přidejte jen soubory, které potřebujete.'
                    : 'K obřadu zatím nejsou přidané žádné soubory.'
                }
              />
            )}
          </>
        }
        notes={
          <>
            <div className="flex min-h-9 items-center justify-between gap-2">
              <h3 className="flex items-center gap-2 text-sm font-semibold">
                <NotebookPen className="size-4 text-action" aria-hidden="true" />
                Poznámky
              </h3>
              <CeremonyEditAction
                id="ceremony-edit-notes"
                label="Upravit poznámky k obřadu"
                onClick={() => void navigate({ to: '/ceremony/materials/notes' })}
              />
            </div>
            <p className="break-words text-sm leading-7 whitespace-pre-line text-muted-foreground">
              {notes || 'Pro vaše přání, nápady a vše, co chcete mít k obřadu zapsané.'}
            </p>
          </>
        }
      />
      {pathname === '/ceremony/materials/notes' ? (
        <CeremonyNotesSheet onClose={() => closeDetail('ceremony-edit-notes')} />
      ) : null}
      {pathname === '/ceremony/materials/summary' ? (
        <CeremonySummarySheet onClose={() => closeDetail('ceremony-open-summary')} />
      ) : null}
      {documentId === 'new' || document ? (
        <CeremonyDocumentSheet
          key={documentId}
          item={document}
          onClose={() => closeDetail(`ceremony-file-${documentId}`)}
        />
      ) : documentId ? (
        <CeremonyMissingSheet
          title="Soubor už tu není"
          onClose={() => closeDetail('ceremony-add-file')}
        />
      ) : null}
    </>
  )
}
